import { useState, useEffect, useMemo } from 'react';
import { SourceDisplayConfig } from '../types/leadSourceRules';
import { api } from '../helpers/apiUtils';

// Auto-generate a color for sources without one
const AUTO_COLORS = [
  '#3b82f6',
  '#ef4444',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#6366f1',
];

export function getAutoColor(sourceKey: string): string {
  let hash = 0;
  for (let i = 0; i < sourceKey.length; i++) {
    hash = ((hash << 5) - hash + sourceKey.charCodeAt(i)) | 0;
  }
  return AUTO_COLORS[Math.abs(hash) % AUTO_COLORS.length];
}

export function getSourceColor(config: SourceDisplayConfig): string {
  return config.color || getAutoColor(config.source_key);
}

export function getDisplayName(
  configMap: Record<string, SourceDisplayConfig>,
  sourceKey: string,
): string {
  return configMap[sourceKey]?.display_name || sourceKey;
}

export function useSourceDisplayConfig(customer: string): {
  config: SourceDisplayConfig[];
  configMap: Record<string, SourceDisplayConfig>;
  loading: boolean;
} {
  const [config, setConfig] = useState<SourceDisplayConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api/lead-source-rules/display-config/${customer}`);
        if (!cancelled && response.data?.success && response.data.data) {
          setConfig(response.data.data);
        } else if (!cancelled) {
          console.error(`Display config fetch returned unexpected shape for customer=${customer}`);
          setConfig([]);
        }
      } catch (error) {
        console.error('Failed to fetch source display config:', error);
        if (!cancelled) {
          setConfig([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [customer]);

  const configMap = useMemo(() => {
    const map: Record<string, SourceDisplayConfig> = {};
    for (const item of config) {
      map[item.source_key] = item;
    }
    return map;
  }, [config]);

  return { config, configMap, loading };
}
